import React, { useState } from 'react'
import {
  H4,
  Card,
  Position,
  PopoverInteractionKind,
  Classes,
  Menu,
  MenuItem,
} from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { Popover } from '@ui'
import Ring from './assets/Ring'
import { Avatar, Toaster, Button } from '@ui'
import Check from './assets/Check'
import Close from './assets/Close'
import useDashboardEmployeeImportList, {
  UseDashboardEmployeeImportListHook,
} from 'components/EmployeeManagement/hooks/useDashboardEmployeeImportList'
// import moment from 'moment'
import api from '@api'
import Success from './assets/Success'
import { IUIButtonProps } from '@ui/buttons/Button'
import PaginationPrototype from './PaginationPrototype'
import { SERVER_DATETIME_FMT } from '@constants'
import css from './dashboard.module.scss'
import { DateUtil, TIME } from '@utils'

export default function ApprovalCard() {
  const hook = useDashboardEmployeeImportList()
  const { imports, loading, reload, pageInfo } = hook
  const [processing, setProcessing] = useState(false)

  const isEmpty = !loading && imports.length === 0
  const isPagination = pageInfo !== null ? pageInfo.totalPages > 1 : false

  const onApprove = (id: string) => {
    if (processing) return

    setProcessing(true)
    api.employee.import
      .approveEmployeeImport(id)
      .then(() => {
        Toaster.success({
          message: 'Approved Successfully',
        })
        reload()
      })
      .catch((e) => {
        Toaster.fail({
          error: e,
        })
      })
      .finally(() => setProcessing(false))
  }

  const onReject = (id: string) => {
    if (processing) return
    setProcessing(true)
    api.employee.import
      .cancelEmployeeImport(id)
      .then(() => {
        Toaster.success({
          message: 'Rejected Successfully',
        })
        reload()
      })
      .catch((error) => {
        Toaster.fail({
          error,
        })
      })
      .finally(() => setProcessing(false))
  }

  if (isEmpty || loading) {
    return null
  }

  return (
    <div className={css.column} style={{ minWidth: 450 }}>
      <Card className="flex flex-col" style={{ width: '100%' }}>
        <div className="flex">
          <Ring />
          <H4 style={{ marginLeft: 10 }}>Action Items</H4>
        </div>
        {isEmpty && <Empty />}
        {loading && <Skeleton />}
        {!loading &&
          imports.map((i, idx) => {
            const isLast = imports.length - 1 === idx

            return (
              <div
                key={i.id + idx}
                className={`flex-col ${isLast ? '' : 'border-b'}`}
                style={{ paddingBottom: '10px', paddingTop: '8px', borderBottomColor: '#CCCCCC' }}
              >
                <div className="text-gray-600" style={{ fontSize: 12 }}>
                  Employee Imports
                </div>
                <div className="flex">
                  <div className="flex-col">
                    <div style={{ fontSize: 14, marginTop: 4, paddingRight: 20 }}>
                      Request: Summarize the changes before actually running the import
                    </div>
                    <div className="flex items-center" style={{ marginTop: 12 }}>
                      <HighlightedDate value={i.autoCancelAt} />
                      <div className="flex" style={{ marginLeft: 19 }}>
                        <Avatar size={Avatar.Size.XXS} name={i.importedBy} />
                        <span className="text-gray-600" style={{ marginLeft: 4, fontSize: 12 }}>
                          {i.importedBy}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="w-1/5 flex items-start ml-auto" style={{ maxWidth: 75 }}>
                    <div
                      style={{ marginTop: 5 }}
                      className="flex w-full justify-between items-center"
                    >
                      <Tooltip2
                        disabled={processing}
                        content={'Approve'}
                        position={Position.BOTTOM}
                        interactionKind={PopoverInteractionKind.HOVER_TARGET_ONLY}
                      >
                        <div
                          className={css.approval_card_container}
                          style={{ filter: processing ? 'opacity(25%)' : undefined }}
                          onClick={() => onApprove(i.id)}
                        >
                          <Check />
                        </div>
                      </Tooltip2>
                      <Tooltip2
                        disabled={processing}
                        content={'Reject'}
                        position={Position.BOTTOM}
                        interactionKind={PopoverInteractionKind.HOVER_TARGET_ONLY}
                      >
                        <div
                          className={css.approval_card_container}
                          style={{ filter: processing ? 'opacity(25%)' : undefined }}
                          onClick={() => onReject(i.id)}
                        >
                          <Close />
                        </div>
                      </Tooltip2>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        {!loading && !isEmpty && isPagination && <Pagination hook={hook} />}
      </Card>
    </div>
  )
}

function Pagination({
  buttonProps = {},
  hook,
  pageDropDownDefaultValues = [5, 10, 20],
}: {
  buttonProps?: IUIButtonProps
  btnText?: string
  hook: UseDashboardEmployeeImportListHook
  pageDropDownDefaultValues?: number[]
}) {
  const { setPerPage, pageInfo, setPage } = hook

  const itemsOnPage =
    pageInfo?.perPage !== undefined &&
    pageInfo?.total !== undefined &&
    pageInfo.perPage > pageInfo.total
      ? pageInfo?.total
      : pageInfo?.perPage
  const totalItems = pageInfo?.total
  const totalPages = pageInfo?.totalPages
  const currentPage = pageInfo?.currentPage

  return (
    <div className="flex flex-row mt-auto">
      <div className="flex flex-row items-center text-12px" style={{ minWidth: '45px' }}>
        {itemsOnPage} of {totalItems}
      </div>
      <div className="ml-3 mr-3 text-12px" style={{ minWidth: '95px' }}>
        Show
        <Popover
          minimal
          className="ml-2"
          position={Position.BOTTOM}
          modifiers={{ arrow: { enabled: false } }}
          content={
            <Menu className="narrow">
              {pageDropDownDefaultValues.map((value) => (
                <MenuItem
                  className="text-12px"
                  key={value}
                  text={value}
                  onClick={() => setPerPage?.(value)}
                />
              ))}
            </Menu>
          }
        >
          <Button
            {...buttonProps}
            style={{ ...buttonProps.style, padding: '0 10px' }}
            rightIconName={'chevron-down'}
            text={itemsOnPage}
          />
        </Popover>
      </div>
      <div className="ml-auto">
        <PaginationPrototype
          totalPages={totalPages || 1}
          current={currentPage || 1}
          onChange={(val) => setPage?.(val)}
        />
      </div>
    </div>
  )
}

function HighlightedDate({ value }: { value: string }) {
  if (!value) return null

  const now = DateUtil()
  const approveDue = DateUtil(value)

  let color = '#FAFAFA'

  if (approveDue.diff(now, TIME.HOURS) > 24 && approveDue.diff(now, TIME.HOURS) < 48) {
    color = '#FAC90F'
  }

  if (approveDue.diff(now, TIME.HOURS) < 24) {
    color = '#E37A7A'
  }

  const text = approveDue.format('DD MMM')

  return (
    <div style={{ padding: '4px 6px', borderRadius: 4, backgroundColor: color }}>Due {text}</div>
  )
}

function Empty() {
  return (
    <div className="flex flex-col items-center justify-start h-full mt-16">
      <Success />
      <div className="pt-4">You’re all caught up! Check back later for updates.</div>
    </div>
  )
}

function Skeleton() {
  return (
    <>
      <div className="pb-8">
        <div className={Classes.SKELETON}>placeholder</div>
        <div className={Classes.SKELETON}>placeholder</div>
        <div className={Classes.SKELETON}>placeholder</div>
      </div>
      <div className="pb-8">
        <div className={Classes.SKELETON}>placeholder</div>
        <div className={Classes.SKELETON}>placeholder</div>
        <div className={Classes.SKELETON}>placeholder</div>
      </div>
      <div className="pb-8">
        <div className={Classes.SKELETON}>placeholder</div>
        <div className={Classes.SKELETON}>placeholder</div>
        <div className={Classes.SKELETON}>placeholder</div>
      </div>
    </>
  )
}
